import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { buildAuthorizeUrl } from '../../api/authApi';
import type { ProviderId } from '../../api/apiSchemas';
import { useProviders } from '../../auth/authQueries';
import { loginProviders } from '../../auth/providerPresentation';
import { ProviderButton } from '../../components/auth/ProviderButton';
import { CenteredCardLayout } from '../../components/layout/CenteredCardLayout';
import { Alert, Skeleton } from '../../components/ui';
import { browserNavigation } from '../../lib/browserNavigation';
import { env } from '../../lib/env';
import { parseLoginParams } from '../../lib/queryParams';
import { ROUTES } from '../../lib/routes';
import { track } from '../../lib/logger';

export default function LoginPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { client, returnRoute } = useMemo(() => parseLoginParams(search), [search]);
  const providersQuery = useProviders(client);
  const [navigating, setNavigating] = useState(false);

  useEffect(() => {
    track('auth_portal_viewed', { route: '/login', ...(client ? { client } : {}) });
  }, [client]);

  useEffect(() => {
    if (providersQuery.isSuccess) {
      track('auth_provider_registry_loaded', { route: '/login' });
    }
  }, [providersQuery.isSuccess]);

  const providers = providersQuery.data ? loginProviders(providersQuery.data.providers) : [];

  function handleActivate(provider: ProviderId): void {
    if (navigating) return;
    setNavigating(true);
    track('auth_login_started', { provider, ...(client ? { client } : {}) });
    // Dev mock: simulate the provider callback client-side (a real navigation
    // would reload and reset the in-memory mock). Stripped from prod builds.
    // `?mock=new` simulates first-time account creation (-> onboarding).
    if (import.meta.env.DEV && env.useMocks) {
      const kind = search.get('mock') === 'new' ? 'new' : 'existing';
      void import('../../mocks/browser').then(({ simulateProviderLogin }) => {
        simulateProviderLogin(kind);
        navigate(`${ROUTES.authComplete}?provider=${provider}`);
      });
      return;
    }
    browserNavigation.assign(buildAuthorizeUrl(provider, { client, returnRoute }));
  }

  return (
    <CenteredCardLayout>
      <div className="signin">
        <img className="signin__icon" src="/realtime-icon.png" alt="" aria-hidden="true" />
        <header className="signin__head">
          <h1>Đăng nhập hoặc tạo tài khoản</h1>
          <p className="signin__subtitle">Chào mừng đến WaterCorp Connect</p>
        </header>

        {navigating ? (
          <Alert className="signin__full" tone="info" live>
            Đang chuyển đến trang đăng nhập…
          </Alert>
        ) : null}

        {providersQuery.isLoading ? (
          <div className="signin__providers" aria-hidden="true">
            <Skeleton height="48px" />
            <Skeleton height="48px" />
          </div>
        ) : null}

        {providersQuery.isError ? (
          <Alert className="signin__full" tone="error">
            Không thể tải phương thức đăng nhập. Vui lòng thử lại sau.
          </Alert>
        ) : null}

        {providersQuery.isSuccess && providers.length === 0 ? (
          <Alert className="signin__full" tone="info">
            Hiện chưa có phương thức đăng nhập khả dụng. Vui lòng liên hệ bộ phận hỗ trợ.
          </Alert>
        ) : null}

        {providers.length > 0 ? (
          <div className="signin__providers" role="group" aria-label="Phương thức đăng nhập">
            {providers.map((provider) => (
              <ProviderButton
                key={provider.id}
                provider={provider}
                disabled={navigating}
                onActivate={handleActivate}
              />
            ))}
          </div>
        ) : null}

        <p className="signin__footer">
          Bằng việc tiếp tục, bạn đồng ý với Điều khoản sử dụng và Chính sách bảo mật.
        </p>
      </div>
    </CenteredCardLayout>
  );
}
